import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { 
  getExits, getExitDetails, initiateExit, updateExit, updateClearance, 
  updateHandover, updateAssets, updateExitInterview, calculateFnfSettlement,
  getExitStats, cancelExit, getEmployees, getDepartments, getDesignations
} from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  UserMinus, Plus, CheckCircle, XCircle, Clock, DollarSign, Calendar, 
  AlertCircle, Eye, Filter, Users, Briefcase, ClipboardCheck, 
  MessageSquare, Package, Calculator, ArrowRight, RefreshCw, Ban, Search, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  initiated: 'bg-blue-100 text-blue-700 border-blue-200',
  notice_period: 'bg-amber-100 text-amber-700 border-amber-200',
  handover_pending: 'bg-orange-100 text-orange-700 border-orange-200',
  clearance_pending: 'bg-purple-100 text-purple-700 border-purple-200',
  fnf_pending: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  cancelled: 'bg-red-100 text-red-700 border-red-200'
};

const exitTypes = [
  { value: 'resignation', label: 'Resignation' },
  { value: 'termination', label: 'Termination' },
  { value: 'retirement', label: 'Retirement' },
  { value: 'end_of_contract', label: 'End of Contract' },
  { value: 'absconding', label: 'Absconding' }
];

const clearanceDepartments = [
  { key: 'reporting_manager', label: 'Reporting Manager', icon: Users },
  { key: 'hr_department', label: 'HR Department', icon: Briefcase },
  { key: 'it_department', label: 'IT Department', icon: Package },
  { key: 'finance_accounts', label: 'Finance & Accounts', icon: DollarSign },
  { key: 'admin_facilities', label: 'Admin & Facilities', icon: ClipboardCheck }
];

const handoverItems = [
  { key: 'duties_handover', label: 'Duties & Responsibilities Handover' },
  { key: 'documents_handover', label: 'Documents & Files Handover' },
  { key: 'passwords_handover', label: 'Passwords & Access Handover' },
  { key: 'client_info_handover', label: 'Client Information Handover' },
  { key: 'pending_tasks_closed', label: 'Pending Tasks Closed/Transferred' }
];

const ExitManagement = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [exits, setExits] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [exitSearch, setExitSearch] = useState('');
  
  // Modal states
  const [showInitiateModal, setShowInitiateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedExit, setSelectedExit] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [employeeSearch, setEmployeeSearch] = useState('');
  
  // Asset Management Integration
  const [pendingAssets, setPendingAssets] = useState([]);
  const [checkingAssets, setCheckingAssets] = useState(false);
  const [exitPendingAssets, setExitPendingAssets] = useState({}); // Map of exit_id -> assets
  
  // Form state for initiating exit
  const [formData, setFormData] = useState({
    employee_id: '',
    exit_type: 'resignation',
    reason: '',
    resignation_date: new Date().toISOString().split('T')[0],
    last_working_day: '',
    notice_waived: false,
    notice_waived_days: 0
  });

  useEffect(() => {
    fetchData();
  }, [filterStatus, filterType]);

  // Check pending assets when employee is selected for exit
  const checkPendingAssets = async (employeeId) => {
    if (!employeeId) {
      setPendingAssets([]);
      return;
    }
    
    setCheckingAssets(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assets/pending-returns/${employeeId}`);
      if (response.ok) {
        const data = await response.json();
        setPendingAssets(data.assets || []);
      }
    } catch (error) {
      console.error('Error checking pending assets:', error);
    } finally {
      setCheckingAssets(false);
    }
  };

  // Check assets for exit detail view
  const checkExitAssets = async (exit) => {
    if (!exit?.employee_id) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assets/pending-returns/${exit.employee_id}`);
      if (response.ok) {
        const data = await response.json();
        setExitPendingAssets(prev => ({
          ...prev,
          [exit.id]: data.assets || []
        }));
      }
    } catch (error) {
      console.error('Error checking exit assets:', error);
    }
  };

  // Effect to check assets when employee changes in form
  useEffect(() => {
    if (formData.employee_id) {
      checkPendingAssets(formData.employee_id);
    }
  }, [formData.employee_id]);

  // Effect to check assets when viewing exit details
  useEffect(() => {
    if (selectedExit) {
      checkExitAssets(selectedExit);
    }
  }, [selectedExit]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [exitsRes, employeesRes, deptRes, desigRes, statsRes] = await Promise.all([
        getExits(filterStatus !== 'all' ? filterStatus : null, filterType !== 'all' ? filterType : null),
        getEmployees(),
        getDepartments(),
        getDesignations(),
        getExitStats()
      ]);
      setExits(exitsRes.data);
      setEmployees(employeesRes.data.filter(e => e.employment_status === 'Working'));
      setDepartments(deptRes.data);
      setDesignations(desigRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleInitiateExit = async () => {
    if (!formData.employee_id) {
      toast.error('Please select an employee');
      return;
    }
    
    try {
      await initiateExit(formData);
      toast.success('Exit process initiated successfully');
      setShowInitiateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate exit');
    }
  };

  const handleViewDetails = async (exitId) => {
    try {
      const response = await getExitDetails(exitId);
      setSelectedExit(response.data);
      setActiveTab('overview');
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load exit details');
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      await updateExit(selectedExit.id, { status: newStatus });
      toast.success('Status updated successfully');
      const response = await getExitDetails(selectedExit.id);
      setSelectedExit(response.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleClearanceUpdate = async (department, status, remarks = '') => {
    try {
      await updateClearance(selectedExit.id, { department, status, remarks });
      toast.success('Clearance updated');
      const response = await getExitDetails(selectedExit.id);
      setSelectedExit(response.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to update clearance');
    }
  };

  const handleHandoverUpdate = async (key, done, remarks = '') => {
    try {
      await updateHandover(selectedExit.id, { [key]: { done, remarks } });
      toast.success('Handover updated');
      const response = await getExitDetails(selectedExit.id);
      setSelectedExit(response.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to update handover');
    }
  };

  const handleAssetUpdate = async (key, returned, remarks = '', recoveryAmount = 0) => {
    try {
      await updateAssets(selectedExit.id, { [key]: { returned, remarks, recovery_amount: recoveryAmount } });
      toast.success('Asset status updated');
      const response = await getExitDetails(selectedExit.id);
      setSelectedExit(response.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to update asset');
    }
  };

  const handleExitInterviewUpdate = async (interviewData) => {
    try {
      await updateExitInterview(selectedExit.id, interviewData);
      toast.success('Exit interview updated');
      const response = await getExitDetails(selectedExit.id);
      setSelectedExit(response.data);
    } catch (error) {
      toast.error('Failed to update exit interview');
    }
  };

  const handleFnfCalculation = async (fnfData) => {
    try {
      await calculateFnfSettlement(selectedExit.id, fnfData);
      toast.success('F&F settlement calculated');
      const response = await getExitDetails(selectedExit.id);
      setSelectedExit(response.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to calculate F&F');
    }
  };

  const handleCancelExit = async () => {
    if (!window.confirm('Are you sure you want to cancel this exit process?')) return;
    try {
      await cancelExit(selectedExit.id);
      toast.success('Exit cancelled successfully');
      setShowDetailModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel exit');
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      exit_type: 'resignation',
      reason: '',
      resignation_date: new Date().toISOString().split('T')[0],
      last_working_day: '',
      notice_waived: false,
      notice_waived_days: 0
    });
    setEmployeeSearch('');
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp?.name || emp?.employee_id || 'Unknown';
  };

  const getDeptName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept?.name || '';
  };

  const getDesigName = (desigId) => {
    const desig = designations.find(d => d.id === desigId);
    return desig?.title || '';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const filteredExits = exits.filter(exit => {
    if (!exitSearch) return true;
    const q = exitSearch.toLowerCase();
    return (exit.employee_name || '').toLowerCase().includes(q) ||
      (exit.employee_code || '').toLowerCase().includes(q) ||
      (exit.department || '').toLowerCase().includes(q) ||
      (exit.designation || '').toLowerCase().includes(q);
  });


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
  };

  // Navigate to Final Settlement with exit data pre-filled
  const goToFinalSettlement = (exitData) => {
    const settlementData = {
      employee_id: exitData.employee_id,
      employee_name: exitData.employee_name,
      employee_code: exitData.employee_code,
      separation_type: exitData.exit_type,
      resignation_date: exitData.resignation_date,
      last_working_date: exitData.last_working_day,
      exit_id: exitData.id,
      from_exit: true
    };
    navigate('/final-settlement', { state: { prefillData: settlementData } });
  };

  const canEdit = user?.role === 'admin' || user?.role === 'hr_manager';
  const canViewFinance = user?.role === 'admin' || user?.role === 'hr_manager' || user?.role === 'accounts_manager';

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="exit-management-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="page-title">Exit Management</h1>
            <p className="text-slate-500 text-sm">Manage employee separations and clearance processes</p>
          </div>
          {canEdit && (
            <Button onClick={() => setShowInitiateModal(true)} className="gap-2" data-testid="initiate-exit-btn">
              <Plus size={18} />
              Initiate Exit
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <UserMinus className="text-blue-600" size={20} />
                  <span className="text-xs text-blue-700">Total</span>
                </div>
                <p className="text-2xl font-bold text-blue-800 mt-1">{stats.total}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Clock className="text-amber-600" size={20} />
                  <span className="text-xs text-amber-700">Notice Period</span>
                </div>
                <p className="text-2xl font-bold text-amber-800 mt-1">{stats.by_status?.notice_period || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Briefcase className="text-orange-600" size={20} />
                  <span className="text-xs text-orange-700">Handover</span>
                </div>
                <p className="text-2xl font-bold text-orange-800 mt-1">{stats.by_status?.handover_pending || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <ClipboardCheck className="text-purple-600" size={20} />
                  <span className="text-xs text-purple-700">Clearance</span>
                </div>
                <p className="text-2xl font-bold text-purple-800 mt-1">{stats.by_status?.clearance_pending || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <DollarSign className="text-cyan-600" size={20} />
                  <span className="text-xs text-cyan-700">F&F Pending</span>
                </div>
                <p className="text-2xl font-bold text-cyan-800 mt-1">{stats.by_status?.fnf_pending || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="text-emerald-600" size={20} />
                  <span className="text-xs text-emerald-700">Completed</span>
                </div>
                <p className="text-2xl font-bold text-emerald-800 mt-1">{stats.by_status?.completed || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Users className="text-slate-600" size={20} />
                  <span className="text-xs text-slate-700">Resignations</span>
                </div>
                <p className="text-2xl font-bold text-slate-800 mt-1">{stats.by_type?.resignations || 0}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <Filter size={16} className="text-slate-500" />
                <span className="text-sm font-medium text-slate-700">Filters:</span>
              </div>
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
                <Input
                  placeholder="Search by name, code, dept..."
                  value={exitSearch}
                  onChange={(e) => setExitSearch(e.target.value)}
                  className="pl-8 w-56 h-9"
                  data-testid="exit-search-input"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="initiated">Initiated</SelectItem>
                  <SelectItem value="notice_period">Notice Period</SelectItem>
                  <SelectItem value="handover_pending">Handover Pending</SelectItem>
                  <SelectItem value="clearance_pending">Clearance Pending</SelectItem>
                  <SelectItem value="fnf_pending">F&F Pending</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-40" data-testid="type-filter">
                  <SelectValue placeholder="Exit Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {exitTypes.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={fetchData} className="gap-2">
                <RefreshCw size={14} />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Exit List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserMinus size={20} />
              Exit Requests ({filteredExits.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : filteredExits.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <UserMinus size={48} className="mx-auto mb-4 text-slate-300" />
                <p>{exitSearch ? 'No matching exit requests found' : 'No exit requests found'}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="exits-table">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Employee</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Department</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Exit Type</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Resignation Date</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Last Working Day</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredExits.map((exit) => (
                      <tr key={exit.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-slate-900">{exit.employee_name || 'N/A'}</p>
                            <p className="text-xs text-slate-500">{exit.employee_code}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-sm text-slate-700">{exit.department || '-'}</p>
                          <p className="text-xs text-slate-500">{exit.designation || '-'}</p>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="capitalize">
                            {exit.exit_type?.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-700">
                          {formatDate(exit.resignation_date)}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-700">
                          {formatDate(exit.last_working_day)}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={`${statusColors[exit.status] || statusColors.initiated} capitalize`}>
                            {exit.status?.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleViewDetails(exit.id)}
                            className="gap-1"
                            data-testid={`view-exit-${exit.id}`}
                          >
                            <Eye size={14} />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Initiate Exit Modal */}
        <Dialog open={showInitiateModal} onOpenChange={(open) => {
          setShowInitiateModal(open);
          if (!open) setEmployeeSearch('');
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserMinus size={20} />
                Initiate Exit Process
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Employee *</label>
                <Select 
                  value={formData.employee_id} 
                  onValueChange={(v) => setFormData({...formData, employee_id: v})}
                >
                  <SelectTrigger data-testid="employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="px-2 pb-2 sticky top-0 bg-white">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder="Search employee..."
                          value={employeeSearch}
                          onChange={(e) => setEmployeeSearch(e.target.value)}
                          className="pl-8 h-9"
                          data-testid="employee-search-input"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    {employees
                      .filter(emp => {
                        if (!employeeSearch) return true;
                        const searchLower = employeeSearch.toLowerCase();
                        const empName = (emp.name || '').toLowerCase();
                        const empId = (emp.employee_id || '').toLowerCase();
                        const empDesig = getDesigName(emp.designation_id).toLowerCase();
                        return empName.includes(searchLower) || 
                               empId.includes(searchLower) || 
                               empDesig.includes(searchLower);
                      })
                      .map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.name || emp.employee_id} - {getDesigName(emp.designation_id)}
                        </SelectItem>
                      ))}
                    {employees.filter(emp => {
                      if (!employeeSearch) return true;
                      const searchLower = employeeSearch.toLowerCase();
                      const empName = (emp.name || '').toLowerCase();
                      const empId = (emp.employee_id || '').toLowerCase();
                      const empDesig = getDesigName(emp.designation_id).toLowerCase();
                      return empName.includes(searchLower) || 
                             empId.includes(searchLower) || 
                             empDesig.includes(searchLower);
                    }).length === 0 && (
                      <div className="py-6 text-center text-sm text-slate-500">
                        No employees found
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Exit Type *</label>
                <Select 
                  value={formData.exit_type} 
                  onValueChange={(v) => setFormData({...formData, exit_type: v})}
                >
                  <SelectTrigger data-testid="exit-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {exitTypes.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Resignation Date</label>
                  <Input 
                    type="date" 
                    value={formData.resignation_date}
                    onChange={(e) => setFormData({...formData, resignation_date: e.target.value})}
                    data-testid="resignation-date-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Last Working Day</label>
                  <Input 
                    type="date" 
                    value={formData.last_working_day}
                    onChange={(e) => setFormData({...formData, last_working_day: e.target.value})}
                    data-testid="lwd-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Reason for Exit</label>
                <Textarea 
                  placeholder="Enter reason for separation..."
                  value={formData.reason}
                  onChange={(e) => setFormData({...formData, reason: e.target.value})}
                  rows={3}
                  data-testid="reason-textarea"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="notice-waived"
                  checked={formData.notice_waived}
                  onCheckedChange={(checked) => setFormData({...formData, notice_waived: checked})}
                />
                <label htmlFor="notice-waived" className="text-sm text-slate-700">
                  Notice Period Waived
                </label>
              </div>
              {formData.notice_waived && (
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Days Waived</label>
                  <Input 
                    type="number"
                    value={formData.notice_waived_days}
                    onChange={(e) => setFormData({...formData, notice_waived_days: parseInt(e.target.value) || 0})}
                  />
                </div>
              )}

              {/* Pending Assets from Asset Management */}
              {formData.employee_id && checkingAssets && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                  <Clock className="text-blue-600 animate-spin" size={18} />
                  <span className="text-sm text-blue-700">Checking assigned assets...</span>
                </div>
              )}

              {formData.employee_id && !checkingAssets && pendingAssets.length > 0 && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="text-amber-600 mt-0.5" size={20} />
                    <div className="flex-1">
                      <h4 className="font-semibold text-amber-800">Company Assets Assigned ({pendingAssets.length})</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        The following assets are currently assigned and must be returned during clearance:
                      </p>
                      <ul className="mt-2 space-y-1">
                        {pendingAssets.slice(0, 5).map((asset, idx) => (
                          <li key={idx} className="text-sm text-amber-700 flex items-center gap-2">
                            <Package size={14} />
                            <span className="font-medium">{asset.asset_name}</span>
                            <span className="text-amber-600 font-mono text-xs">({asset.serial_number})</span>
                          </li>
                        ))}
                        {pendingAssets.length > 5 && (
                          <li className="text-sm text-amber-600 italic">...and {pendingAssets.length - 5} more</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {formData.employee_id && !checkingAssets && pendingAssets.length === 0 && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                  <CheckCircle className="text-green-600" size={18} />
                  <span className="text-sm text-green-700">No company assets currently assigned to this employee.</span>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInitiateModal(false)}>Cancel</Button>
              <Button onClick={handleInitiateExit} data-testid="confirm-initiate-btn">
                Initiate Exit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Exit Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserMinus size={20} />
                Exit Details - {selectedExit?.employee_name}
                <Badge className={`${statusColors[selectedExit?.status] || ''} capitalize ml-2`}>
                  {selectedExit?.status?.replace('_', ' ')}
                </Badge>
              </DialogTitle>
            </DialogHeader>
            
            {selectedExit && (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-5 mb-4">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="handover">Handover</TabsTrigger>
                  <TabsTrigger value="clearance">Clearance</TabsTrigger>
                  <TabsTrigger value="assets">Assets</TabsTrigger>
                  <TabsTrigger value="fnf">F&F Settlement</TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-500">Employee Code</p>
                      <p className="font-medium">{selectedExit.employee_code || '-'}</p>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-500">Department</p>
                      <p className="font-medium">{selectedExit.department || '-'}</p>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-500">Designation</p>
                      <p className="font-medium">{selectedExit.designation || '-'}</p>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-500">Exit Type</p>
                      <p className="font-medium capitalize">{selectedExit.exit_type?.replace('_', ' ')}</p>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-500">Resignation Date</p>
                      <p className="font-medium">{formatDate(selectedExit.resignation_date)}</p>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-500">Last Working Day</p>
                      <p className="font-medium">{formatDate(selectedExit.last_working_day)}</p>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-500">Notice Period</p>
                      <p className="font-medium">{selectedExit.notice_period_days || 30} days</p>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-500">Date of Joining</p>
                      <p className="font-medium">{formatDate(selectedExit.date_of_joining)}</p>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-500">Monthly Salary</p>
                      <p className="font-medium">{formatCurrency(selectedExit.salary)}</p>
                    </div>
                  </div>
                  
                  {selectedExit.reason && (
                    <div className="bg-amber-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-amber-800 mb-1">Reason for Exit</p>
                      <p className="text-sm text-amber-700">{selectedExit.reason}</p>
                    </div>
                  )}

                  {/* Exit Interview Section */}
                  <ExitInterviewSection 
                    interview={selectedExit.exit_interview} 
                    onUpdate={handleExitInterviewUpdate}
                    canEdit={canEdit}
                  />

                  {/* Status Update Actions */}
                  {canEdit && selectedExit.status !== 'completed' && selectedExit.status !== 'cancelled' && (
                    <div className="flex gap-2 pt-4 border-t">
                      {selectedExit.status === 'initiated' && (
                        <Button onClick={() => handleUpdateStatus('notice_period')} className="gap-2">
                          <ArrowRight size={16} />
                          Move to Notice Period
                        </Button>
                      )}
                      {selectedExit.status === 'notice_period' && (
                        <Button onClick={() => handleUpdateStatus('handover_pending')} className="gap-2">
                          <ArrowRight size={16} />
                          Move to Handover
                        </Button>
                      )}
                      <Button variant="destructive" onClick={handleCancelExit} className="gap-2 ml-auto">
                        <Ban size={16} />
                        Cancel Exit
                      </Button>
                    </div>
                  )}
                </TabsContent>

                {/* Handover Tab */}
                <TabsContent value="handover" className="space-y-4">
                  <div className="space-y-3">
                    {handoverItems.map((item) => {
                      const handover = selectedExit.handover_checklist?.[item.key] || { done: false, remarks: '' };
                      return (
                        <HandoverItem 
                          key={item.key}
                          item={item}
                          data={handover}
                          onUpdate={(done, remarks) => handleHandoverUpdate(item.key, done, remarks)}
                          canEdit={canEdit}
                        />
                      );
                    })}
                  </div>
                  {canEdit && selectedExit.status === 'handover_pending' && (
                    <div className="pt-4 border-t">
                      <Button onClick={() => handleUpdateStatus('clearance_pending')} className="gap-2">
                        <ArrowRight size={16} />
                        Move to Clearance
                      </Button>
                    </div>
                  )}
                </TabsContent>

                {/* Clearance Tab */}
                <TabsContent value="clearance" className="space-y-4">
                  <div className="space-y-3">
                    {clearanceDepartments.map((dept) => {
                      const clearance = selectedExit.clearances?.[dept.key] || { status: 'pending', remarks: '' };
                      return (
                        <ClearanceItem 
                          key={dept.key}
                          dept={dept}
                          data={clearance}
                          onUpdate={(status, remarks) => handleClearanceUpdate(dept.key, status, remarks)}
                          canEdit={canEdit}
                        />
                      );
                    })}
                  </div>
                  {canEdit && selectedExit.status === 'clearance_pending' && (
                    <div className="pt-4 border-t space-y-3">
                      {/* Asset Warning */}
                      {(exitPendingAssets[selectedExit.id]?.length || 0) > 0 && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                          <AlertCircle className="text-red-600 mt-0.5" size={18} />
                          <div>
                            <p className="text-sm font-medium text-red-800">
                              Cannot proceed to F&F - {exitPendingAssets[selectedExit.id]?.length} asset(s) pending return
                            </p>
                            <p className="text-xs text-red-600 mt-1">
                              Go to Assets tab or Asset Management to process returns first.
                            </p>
                          </div>
                        </div>
                      )}
                      <Button 
                        onClick={() => {
                          const pendingCount = exitPendingAssets[selectedExit.id]?.length || 0;
                          if (pendingCount > 0) {
                            toast.error(`Cannot proceed: ${pendingCount} asset(s) must be returned first`);
                            return;
                          }
                          handleUpdateStatus('fnf_pending');
                        }} 
                        className="gap-2"
                        disabled={(exitPendingAssets[selectedExit.id]?.length || 0) > 0}
                      >
                        <ArrowRight size={16} />
                        Move to F&F Settlement
                      </Button>
                    </div>
                  )}
                </TabsContent>

                {/* Assets Tab - Integrated with Asset Management */}
                <TabsContent value="assets" className="space-y-4">
                  {/* Assets from Asset Management Module */}
                  {selectedExit && (
                    <AssetsFromAssetManagement 
                      exitId={selectedExit.id}
                      employeeId={selectedExit.employee_id}
                      pendingAssets={exitPendingAssets[selectedExit.id] || []}
                      canEdit={canEdit}
                    />
                  )}
                </TabsContent>

                {/* F&F Settlement Tab */}
                <TabsContent value="fnf" className="space-y-4">
                  {/* Link to Final Settlement */}
                  <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200 flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-indigo-800 flex items-center gap-2">
                        <DollarSign size={18} />
                        Full & Final Settlement
                      </h4>
                      <p className="text-sm text-indigo-600 mt-1">
                        Create detailed F&F settlement with salary calculations, deductions, and approvals
                      </p>
                    </div>
                    <Button 
                      onClick={() => goToFinalSettlement(selectedExit)}
                      className="gap-2 bg-indigo-600 hover:bg-indigo-700"
                      data-testid="go-to-final-settlement-btn"
                    >
                      <ExternalLink size={16} />
                      Go to Final Settlement
                    </Button>
                  </div>
                  
                  <FnfSettlementSection 
                    fnf={selectedExit.fnf_settlement}
                    salary={selectedExit.salary || 0}
                    onCalculate={handleFnfCalculation}
                    canEdit={canViewFinance}
                    status={selectedExit.status}
                  />
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

// Handover Item Component
const HandoverItem = ({ item, data, onUpdate, canEdit }) => {
  const [remarks, setRemarks] = useState(data.remarks || '');
  
  return (
    <div className={`p-4 rounded-lg border ${data.done ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Checkbox 
            checked={data.done}
            onCheckedChange={(checked) => onUpdate(checked, remarks)}
            disabled={!canEdit}
          />
          <span className={`font-medium ${data.done ? 'text-emerald-700' : 'text-slate-700'}`}>
            {item.label}
          </span>
        </div>
        {data.done && <CheckCircle className="text-emerald-500" size={20} />}
      </div>
      {canEdit && (
        <div className="mt-2 ml-7">
          <Input 
            placeholder="Add remarks..."
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            onBlur={() => onUpdate(data.done, remarks)}
            className="text-sm"
          />
        </div>
      )}
      {!canEdit && data.remarks && (
        <p className="mt-2 ml-7 text-sm text-slate-600">{data.remarks}</p>
      )}
    </div>
  );
};

// Clearance Item Component
const ClearanceItem = ({ dept, data, onUpdate, canEdit }) => {
  const [remarks, setRemarks] = useState(data.remarks || '');
  const Icon = dept.icon;
  
  const statusBg = {
    pending: 'bg-amber-50 border-amber-200',
    approved: 'bg-emerald-50 border-emerald-200',
    rejected: 'bg-red-50 border-red-200'
  };
  
  return (
    <div className={`p-4 rounded-lg border ${statusBg[data.status] || statusBg.pending}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon size={20} className="text-slate-600" />
          <span className="font-medium text-slate-700">{dept.label}</span>
        </div>
        <div className="flex items-center gap-2">
          {data.status === 'approved' && <CheckCircle className="text-emerald-500" size={20} />}
          {data.status === 'rejected' && <XCircle className="text-red-500" size={20} />}
          {data.status === 'pending' && <Clock className="text-amber-500" size={20} />}
          <Badge className={`capitalize ${
            data.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
            data.status === 'rejected' ? 'bg-red-100 text-red-700' :
            'bg-amber-100 text-amber-700'
          }`}>
            {data.status}
          </Badge>
        </div>
      </div>
      {canEdit && data.status === 'pending' && (
        <div className="mt-3 space-y-2">
          <Input 
            placeholder="Add remarks..."
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            className="text-sm"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => onUpdate('approved', remarks)} className="bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle size={14} className="mr-1" />
              Approve
            </Button>
            <Button size="sm" variant="destructive" onClick={() => onUpdate('rejected', remarks)}>
              <XCircle size={14} className="mr-1" />
              Reject
            </Button>
          </div>
        </div>
      )}
      {data.remarks && (
        <p className="mt-2 text-sm text-slate-600">Remarks: {data.remarks}</p>
      )}
      {data.approved_at && (
        <p className="mt-1 text-xs text-slate-500">
          Updated: {new Date(data.approved_at).toLocaleDateString('en-IN')}
        </p>
      )}
    </div>
  );
};

// Assets from Asset Management Component (Integrated)
const AssetsFromAssetManagement = ({ exitId, employeeId, pendingAssets, canEdit }) => {
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [allAssets, setAllAssets] = useState([]); // All assets including returned
  const [searchTerm, setSearchTerm] = useState('');
  const [showAllAssets, setShowAllAssets] = useState(true);
  
  useEffect(() => {
    // Fetch all assets assigned to this employee
    fetchAllEmployeeAssets();
  }, [employeeId]);

  const fetchAllEmployeeAssets = async () => {
    setLoading(true);
    try {
      // Fetch all asset assignments for this employee
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/asset-assignments?employee_id=${employeeId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAllAssets(data || []);
        // Filter pending (not returned) assets
        const pending = data.filter(a => !a.returned_date);
        setAssets(pending);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
      // Fallback to pending assets from props
      setAssets(pendingAssets || []);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
  };

  const refreshAssets = async () => {
    await fetchAllEmployeeAssets();
  };

  // Filter assets based on search term
  const displayAssets = showAllAssets ? allAssets : assets;
  const filteredAssets = displayAssets.filter(asset => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (asset.asset_name || '').toLowerCase().includes(searchLower) ||
      (asset.asset_type || '').toLowerCase().includes(searchLower) ||
      (asset.serial_number || '').toLowerCase().includes(searchLower) ||
      (asset.asset_tag || '').toLowerCase().includes(searchLower)
    );
  });

  const pendingCount = assets.length;
  const returnedCount = allAssets.filter(a => a.returned_date).length;

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 rounded-lg border bg-blue-50 border-blue-200">
          <div className="text-2xl font-bold text-blue-700">{allAssets.length}</div>
          <div className="text-xs text-blue-600">Total Assigned</div>
        </div>
        <div className={`p-3 rounded-lg border ${pendingCount > 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
          <div className={`text-2xl font-bold ${pendingCount > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{pendingCount}</div>
          <div className={`text-xs ${pendingCount > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>Pending Return</div>
        </div>
        <div className="p-3 rounded-lg border bg-emerald-50 border-emerald-200">
          <div className="text-2xl font-bold text-emerald-700">{returnedCount}</div>
          <div className="text-xs text-emerald-600">Returned</div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
          <input
            type="text"
            placeholder="Search assets by name, type, serial number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={showAllAssets ? "default" : "outline"}
            size="sm"
            onClick={() => setShowAllAssets(true)}
          >
            All ({allAssets.length})
          </Button>
          <Button
            variant={!showAllAssets ? "default" : "outline"}
            size="sm"
            onClick={() => setShowAllAssets(false)}
          >
            Pending ({pendingCount})
          </Button>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={refreshAssets}
          disabled={loading}
          className="gap-1"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      {/* Assets Table */}
      {loading ? (
        <div className="flex items-center justify-center p-8">
          <RefreshCw className="animate-spin text-slate-400" size={24} />
          <span className="ml-2 text-slate-500">Loading assets...</span>
        </div>
      ) : filteredAssets.length > 0 ? (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3 font-medium text-slate-600">Asset</th>
                <th className="text-left p-3 font-medium text-slate-600">Type</th>
                <th className="text-left p-3 font-medium text-slate-600">Serial/Tag</th>
                <th className="text-left p-3 font-medium text-slate-600">Assigned</th>
                <th className="text-left p-3 font-medium text-slate-600">Returned</th>
                <th className="text-center p-3 font-medium text-slate-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredAssets.map((asset, idx) => (
                <tr key={idx} className="hover:bg-slate-50">
                  <td className="p-3">
                    <div className="font-medium">{asset.asset_name || 'N/A'}</div>
                    {asset.brand && <div className="text-xs text-slate-500">{asset.brand} {asset.model}</div>}
                  </td>
                  <td className="p-3 text-slate-600">{asset.asset_type || asset.category || '-'}</td>
                  <td className="p-3">
                    <div className="font-mono text-xs">{asset.serial_number || '-'}</div>
                    {asset.asset_tag && <div className="text-xs text-slate-500">{asset.asset_tag}</div>}
                  </td>
                  <td className="p-3 text-slate-600">{formatDate(asset.assigned_date)}</td>
                  <td className="p-3 text-slate-600">{formatDate(asset.returned_date)}</td>
                  <td className="p-3 text-center">
                    {asset.returned_date ? (
                      <Badge className="bg-emerald-100 text-emerald-700">Returned</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center p-8 border rounded-lg bg-slate-50">
          <Package className="mx-auto text-slate-300 mb-2" size={40} />
          <p className="text-slate-500">
            {searchTerm ? 'No assets match your search' : 'No assets assigned to this employee'}
          </p>
        </div>
      )}

      {/* Action Info */}
      {pendingCount > 0 && canEdit && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-700 flex items-center gap-2">
            <AlertCircle size={14} />
            <span>
              <strong>{pendingCount} asset(s)</strong> must be returned before FnF can be processed. 
              Go to <strong>Asset Management → Assignments</strong> to mark assets as returned.
            </span>
          </p>
        </div>
      )}

      {pendingCount === 0 && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <p className="text-sm text-emerald-700 flex items-center gap-2">
            <CheckCircle size={14} />
            <span>All assets have been returned. FnF can proceed.</span>
          </p>
        </div>
      )}
    </div>
  );
};

// Exit Interview Section Component
const ExitInterviewSection = ({ interview, onUpdate, canEdit }) => {
  const [formData, setFormData] = useState({
    conducted: interview?.conducted || false,
    feedback: interview?.feedback || '',
    reason_for_leaving: interview?.reason_for_leaving || '',
    would_rejoin: interview?.would_rejoin || null,
    suggestions: interview?.suggestions || ''
  });

  const handleSave = () => {
    onUpdate(formData);
  };

  return (
    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-blue-800 flex items-center gap-2">
          <MessageSquare size={18} />
          Exit Interview
        </h4>
        <Badge className={formData.conducted ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
          {formData.conducted ? 'Conducted' : 'Pending'}
        </Badge>
      </div>
      
      {canEdit ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox 
              checked={formData.conducted}
              onCheckedChange={(checked) => setFormData({...formData, conducted: checked})}
            />
            <label className="text-sm text-slate-700">Interview Conducted</label>
          </div>
          {formData.conducted && (
            <>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Reason for Leaving</label>
                <Textarea 
                  value={formData.reason_for_leaving}
                  onChange={(e) => setFormData({...formData, reason_for_leaving: e.target.value})}
                  rows={2}
                  className="text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Feedback</label>
                <Textarea 
                  value={formData.feedback}
                  onChange={(e) => setFormData({...formData, feedback: e.target.value})}
                  rows={2}
                  className="text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Would Rejoin?</label>
                <Select 
                  value={formData.would_rejoin === null ? '' : String(formData.would_rejoin)}
                  onValueChange={(v) => setFormData({...formData, would_rejoin: v === 'true'})}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Suggestions</label>
                <Textarea 
                  value={formData.suggestions}
                  onChange={(e) => setFormData({...formData, suggestions: e.target.value})}
                  rows={2}
                  className="text-sm"
                />
              </div>
              <Button size="sm" onClick={handleSave}>Save Interview</Button>
            </>
          )}
        </div>
      ) : (
        interview?.conducted && (
          <div className="space-y-2 text-sm">
            {interview.reason_for_leaving && (
              <p><span className="text-slate-500">Reason:</span> {interview.reason_for_leaving}</p>
            )}
            {interview.feedback && (
              <p><span className="text-slate-500">Feedback:</span> {interview.feedback}</p>
            )}
            {interview.suggestions && (
              <p><span className="text-slate-500">Suggestions:</span> {interview.suggestions}</p>
            )}
          </div>
        )
      )}
    </div>
  );
};

// F&F Settlement Section Component
const FnfSettlementSection = ({ fnf, salary, onCalculate, canEdit, status }) => {
  const [formData, setFormData] = useState({
    salary_till_lwd: fnf?.salary_till_lwd || 0,
    leave_encashment: fnf?.leave_encashment || 0,
    bonus_incentives: fnf?.bonus_incentives || 0,
    gratuity: fnf?.gratuity || 0,
    notice_pay_deduction: fnf?.notice_pay_deduction || 0,
    advance_recovery: fnf?.advance_recovery || 0,
    other_deductions: fnf?.other_deductions || 0,
    processed: fnf?.processed || false
  });

  const totalEarnings = formData.salary_till_lwd + formData.leave_encashment + 
                        formData.bonus_incentives + formData.gratuity;
  const totalDeductions = formData.notice_pay_deduction + formData.advance_recovery + 
                          formData.other_deductions + (fnf?.asset_recovery || 0);
  const netPayable = totalEarnings - totalDeductions;

  const handleCalculate = () => {
    onCalculate(formData);
  };

  const handleProcess = () => {
    if (!window.confirm('Are you sure you want to process this F&F settlement?')) return;
    onCalculate({ ...formData, processed: true });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {/* Earnings */}
        <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
          <h4 className="font-medium text-emerald-800 mb-3 flex items-center gap-2">
            <DollarSign size={18} />
            Earnings
          </h4>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Salary till LWD</label>
              <Input 
                type="number"
                value={formData.salary_till_lwd}
                onChange={(e) => setFormData({...formData, salary_till_lwd: parseFloat(e.target.value) || 0})}
                disabled={!canEdit || fnf?.processed}
              />
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Leave Encashment</label>
              <Input 
                type="number"
                value={formData.leave_encashment}
                onChange={(e) => setFormData({...formData, leave_encashment: parseFloat(e.target.value) || 0})}
                disabled={!canEdit || fnf?.processed}
              />
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Bonus & Incentives</label>
              <Input 
                type="number"
                value={formData.bonus_incentives}
                onChange={(e) => setFormData({...formData, bonus_incentives: parseFloat(e.target.value) || 0})}
                disabled={!canEdit || fnf?.processed}
              />
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Gratuity</label>
              <Input 
                type="number"
                value={formData.gratuity}
                onChange={(e) => setFormData({...formData, gratuity: parseFloat(e.target.value) || 0})}
                disabled={!canEdit || fnf?.processed}
              />
            </div>
            <div className="pt-2 border-t border-emerald-300">
              <p className="text-sm font-medium text-emerald-800">
                Total Earnings: ₹{totalEarnings.toLocaleString('en-IN')}
              </p>
            </div>
          </div>
        </div>

        {/* Deductions */}
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <h4 className="font-medium text-red-800 mb-3 flex items-center gap-2">
            <Calculator size={18} />
            Deductions
          </h4>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Notice Pay Deduction</label>
              <Input 
                type="number"
                value={formData.notice_pay_deduction}
                onChange={(e) => setFormData({...formData, notice_pay_deduction: parseFloat(e.target.value) || 0})}
                disabled={!canEdit || fnf?.processed}
              />
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Asset Recovery</label>
              <Input 
                type="number"
                value={fnf?.asset_recovery || 0}
                disabled
                className="bg-slate-100"
              />
              <p className="text-xs text-slate-500 mt-1">Auto-calculated from assets tab</p>
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Advance Recovery</label>
              <Input 
                type="number"
                value={formData.advance_recovery}
                onChange={(e) => setFormData({...formData, advance_recovery: parseFloat(e.target.value) || 0})}
                disabled={!canEdit || fnf?.processed}
              />
            </div>
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Other Deductions</label>
              <Input 
                type="number"
                value={formData.other_deductions}
                onChange={(e) => setFormData({...formData, other_deductions: parseFloat(e.target.value) || 0})}
                disabled={!canEdit || fnf?.processed}
              />
            </div>
            <div className="pt-2 border-t border-red-300">
              <p className="text-sm font-medium text-red-800">
                Total Deductions: ₹{totalDeductions.toLocaleString('en-IN')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Net Settlement */}
      <div className={`p-4 rounded-lg border ${netPayable >= 0 ? 'bg-emerald-100 border-emerald-300' : 'bg-red-100 border-red-300'}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-600">Net Settlement Amount</p>
            <p className={`text-2xl font-bold ${netPayable >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              ₹{netPayable.toLocaleString('en-IN')}
            </p>
          </div>
          {fnf?.processed ? (
            <Badge className="bg-emerald-600 text-white text-lg px-4 py-2">
              <CheckCircle size={18} className="mr-2" />
              Processed
            </Badge>
          ) : (
            canEdit && status === 'fnf_pending' && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleCalculate}>
                  <Calculator size={16} className="mr-2" />
                  Save Calculation
                </Button>
                <Button onClick={handleProcess} className="bg-emerald-600 hover:bg-emerald-700">
                  <CheckCircle size={16} className="mr-2" />
                  Process F&F
                </Button>
              </div>
            )
          )}
        </div>
        {fnf?.processed_at && (
          <p className="text-xs text-slate-500 mt-2">
            Processed on: {new Date(fnf.processed_at).toLocaleDateString('en-IN')}
          </p>
        )}
      </div>
    </div>
  );
};

export default ExitManagement;
